/**
 * High-level auth client — encapsulates the PKCE flow plus token refresh.
 *
 * Surface:
 *   - beginLogin(): kicks off auth-code+PKCE redirect to OIDC issuer
 *   - completeLogin(): exchanges code+verifier for tokens at /auth/callback
 *   - refresh(): rotates the refresh_token, returns fresh access_token
 *   - logout(): clears tokens locally; optionally also hits end_session endpoint
 *
 * State that survives the redirect (verifier, state, target return path) is
 * stored in sessionStorage under `tacosai-admin-pkce` with a 10-min TTL.
 */

import { OIDC_CONFIG, getRedirectUri } from './oidc-config.js';
import { deriveCodeChallenge, generateCodeVerifier, generateState } from './pkce.js';
import { clearTokens, getTokens, setTokens, tokensFromResponse } from './token-store.js';

const PKCE_STATE_KEY = 'tacosai-admin-pkce';
const PKCE_STATE_TTL_MS = 10 * 60 * 1000;

interface PkceState {
  verifier: string;
  state: string;
  return_to: string;
  created_at: number;
}

function savePkceState(s: PkceState): void {
  sessionStorage.setItem(PKCE_STATE_KEY, JSON.stringify(s));
}

function loadPkceState(): PkceState | null {
  const raw = sessionStorage.getItem(PKCE_STATE_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as PkceState;
    if (Date.now() - s.created_at > PKCE_STATE_TTL_MS) {
      sessionStorage.removeItem(PKCE_STATE_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

function clearPkceState(): void {
  sessionStorage.removeItem(PKCE_STATE_KEY);
}

/**
 * Step 1: kick off the auth flow. Generates PKCE pair, stores verifier +
 * state, then redirects the browser to the OIDC authorization endpoint.
 *
 * No `prompt=login` — with the end_session-based logout (see logout()
 * below), the OIDC Session is destroyed at sign-out so the next
 * /oidc/auth call lands a returning user on a fresh Session normally.
 * Forcing prompt=login here would (a) be redundant after a clean
 * end_session and (b) interact destructively with a live Session if
 * end_session ever failed silently — exactly the failure mode the R20
 * audit traced. Trust the spec, no compensating controls.
 *
 * @param returnTo path to return to after callback completes (default: '/')
 */
export async function beginLogin(returnTo = '/'): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await deriveCodeChallenge(verifier);
  const state = generateState();

  savePkceState({
    verifier,
    state,
    return_to: returnTo,
    created_at: Date.now(),
  });

  const url = new URL(OIDC_CONFIG.authorization_endpoint);
  url.searchParams.set('client_id', OIDC_CONFIG.client_id);
  url.searchParams.set('redirect_uri', getRedirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', OIDC_CONFIG.scopes);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');

  window.location.assign(url.toString());
}

/**
 * Step 2: callback handler — verify state, exchange code for tokens.
 *
 * Returns the return_to path so the callback page can navigate to it.
 * Throws on any verification or exchange failure.
 */
export async function completeLogin(search: string): Promise<string> {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  if (error) {
    throw new Error(`OIDC error: ${error} ${params.get('error_description') ?? ''}`.trim());
  }
  if (!code || !state) {
    throw new Error('Missing code or state in callback');
  }

  const saved = loadPkceState();
  if (!saved) {
    throw new Error('No PKCE state — login may have timed out (10 min limit)');
  }
  if (saved.state !== state) {
    throw new Error('State mismatch — possible CSRF; aborting');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: OIDC_CONFIG.client_id,
    code,
    code_verifier: saved.verifier,
    redirect_uri: getRedirectUri(),
  });

  const res = await fetch(OIDC_CONFIG.token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Token exchange failed: ${res.status} ${txt}`);
  }
  const json = (await res.json()) as Record<string, unknown>;
  setTokens(tokensFromResponse(json));
  clearPkceState();
  return saved.return_to;
}

/**
 * Refresh the access_token using the stored refresh_token.
 *
 * Per docs/26-oidc-design.md "refresh-token rotation" — every refresh
 * returns a NEW refresh_token; the old one is invalidated server-side. If
 * the server detects re-use of an already-consumed refresh_token, all
 * tokens for that user-client pair are revoked.
 *
 * Returns the new access_token on success; throws on failure (caller
 * should redirect to login).
 */
export async function refresh(): Promise<string> {
  const t = getTokens();
  if (!t) throw new Error('No tokens to refresh');

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: OIDC_CONFIG.client_id,
    refresh_token: t.refresh_token,
  });

  const res = await fetch(OIDC_CONFIG.token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    clearTokens();
    const txt = await res.text().catch(() => '');
    throw new Error(`Refresh failed: ${res.status} ${txt}`);
  }
  const json = (await res.json()) as Record<string, unknown>;
  const fresh = tokensFromResponse(json);
  setTokens(fresh);
  return fresh.access_token;
}

/**
 * Logout — hand off to the OIDC end_session endpoint.
 *
 * Why end_session (not /oidc/revoke):
 *   - revoke kills tokens + grant but leaves the server-side Session
 *     row AND the `_session` browser cookie alive. The next sign-in
 *     then trips oidc-provider's Session-reconciliation logic with
 *     SessionNotFound ("session principal changed" / "interaction
 *     session and authentication session mismatch"). See R20 audit.
 *   - end_session does what revoke does AND destroys the Session +
 *     clears the cookie via Set-Cookie max-age=0, then redirects back
 *     to a registered post_logout_redirect_uri. One server round-trip.
 *
 * Pre-condition: the tacosai-admin client must list our /login URL
 * under post_logout_redirect_uris (it does — see clients.ts).
 *
 * Fallback: if no id_token is available (e.g. session-store was already
 * wiped), we just clear local state and navigate to /login. The next
 * /oidc/auth pass will re-issue a Session normally if the user signs
 * in again.
 */
export async function logout(): Promise<void> {
  const t = getTokens();
  const idToken = t?.id_token;

  // Clear local state up-front so a back-button can't recover tokens.
  clearTokens();
  clearPkceState();

  if (idToken) {
    const url = new URL(OIDC_CONFIG.end_session_endpoint);
    url.searchParams.set('id_token_hint', idToken);
    url.searchParams.set('post_logout_redirect_uri', `${window.location.origin}/login`);
    url.searchParams.set('client_id', OIDC_CONFIG.client_id);
    window.location.assign(url.toString());
    return;
  }

  // No id_token — degrade to local-only logout. Server-side Session may
  // still be alive but next sign-in will start a fresh one cleanly.
  window.location.assign('/login');
}
